import type { ConversationSummary, ServiceWindow } from '@zenda/api-contract';

/**
 * Os limites que respondem "o que eu preciso responder agora".
 *
 * ── Por que num lugar só ──
 * Eles estavam repetidos: `120` aparecia em quatro arquivos — no filtro de urgentes do
 * inbox, no selo da linha, no painel e na barra da janela — e `60` em dois, com uma das
 * cópias dentro de `waitingLevel`, que é formatação e não regra.
 *
 * Concordavam por coincidência. Mudar o limite num lugar fazia o selo ficar âmbar
 * enquanto o filtro não considerava a conversa urgente, ou o painel destacar o que o
 * inbox não destacava — e ninguém descobriria isso lendo um dos arquivos.
 *
 * Os números não são arbitrários e não são a mesma coisa: uma hora sem resposta é uma
 * reclamação em formação; duas horas de janela restante é o tempo que ainda dá para
 * resolver sem gastar template, porque depois disso a janela de 24 h fecha e falar
 * custa dinheiro.
 */
export const ESPERA_LONGA_MIN = 60;

/** Espera que deixou de ser atraso e virou problema. */
export const ESPERA_TARDE_MIN = 240;

/** Janela de 24 h prestes a fechar: depois dela, falar exige template. */
export const JANELA_APERTADA_MIN = 120;

/** Nível de urgência da espera. Governa a cor — e só há três para não virar arco-íris. */
export function nivelDeEspera(minutos: number | null): 'ok' | 'warn' | 'late' | null {
  if (minutos === null) return null;
  if (minutos >= ESPERA_TARDE_MIN) return 'late';
  if (minutos >= ESPERA_LONGA_MIN) return 'warn';
  return 'ok';
}

/** A janela está aberta e vai fechar logo. Fechada não está apertando: já fechou. */
export function janelaApertando(w: ServiceWindow): boolean {
  return w.open && w.minutesRemaining !== null && w.minutesRemaining < JANELA_APERTADA_MIN;
}

/**
 * "Precisa de mim agora" — o que antes eram dois filtros, `late` e `closing`.
 *
 * Eles nunca foram do mesmo eixo que "Todas / Aguardando / Resolvidas": estes descrevem
 * o ESTADO da conversa e se excluem; aqueles descrevem URGÊNCIA e atravessam qualquer
 * estado. Empilhados na mesma fila de botões, os cinco não cabiam em português —
 * medido, 396 px numa faixa de 350 — e alargar a coluna até caber tiraria espaço da
 * conversa, que é onde o trabalho acontece.
 *
 * Juntá-los não perde informação porque a PERGUNTA é uma só: o que eu respondo agora?
 * Paciente esperando há horas e janela prestes a fechar levam à mesma ação. E a
 * distinção entre os dois motivos continua visível na linha, onde ela importa — o selo
 * diz "janela fechada", "20 h" ou "fecha em 38min".
 */
export function precisaDeAtencao(c: ConversationSummary): boolean {
  const esperandoDemais = c.minutesWaiting !== null && c.minutesWaiting >= ESPERA_LONGA_MIN;
  return esperandoDemais || janelaApertando(c.serviceWindow);
}

/** Mesma conta do tile "Conversas aguardando" (rotas-painel.ts): resolvida nao aguarda. */
export function aguardaResposta(c: Pick<ConversationSummary, 'awaitingReply' | 'status'>): boolean {
  return c.awaitingReply && c.status !== 'resolved';
}
